import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { fetchData } from '../../apiService';
import useSessionManager from '../../hooks/useSessionManager';
import {
  Receipt,
  Payment,
  ShoppingCart,
  Description,
  Undo,
  AccountBalanceWallet,
  Close as CloseIcon,
  TrendingUp,
  Inventory
} from '@mui/icons-material';
import { Dialog, DialogTitle, DialogContent, Box, Grid, Typography, Chip, Button, IconButton } from '@mui/material';
import SaleModal from '../pages/Sale/SaleModal';
import PurchaseModal from '../pages/Purchase/PurchaseModal';
import SaleReturnModal from '../pages/SaleReturn/SaleReturnModal';
import PaymentReceiptsModal from '../pages/PaymentReceipts/PaymentReceiptsModal';
import JournalEntryModal from '../pages/JournalEntry/JournalEntryModal';
import ModernLoadingOverlay from '../common/ModernLoadingOverlay';
import DashboardLoader from '../common/DashboardLoader';
import './Home.css';

const Home = () => {
  const { companyId, branchId, token } = useAuth();
  const { validateSession } = useSessionManager();
  const history = useHistory();
  const [loading, setLoading] = useState(true);

  const [transactionStats, setTransactionStats] = useState({
    sales: { count: 0, amount: 0 },
    purchases: { count: 0, amount: 0 },
    saleReturns: { count: 0, amount: 0 },
    purchaseReturns: { count: 0, amount: 0 },
    paymentReceipts: { count: 0, amount: 0 },
    journalEntries: { count: 0, amount: 0 }
  });

  const [recentActivities, setRecentActivities] = useState([]);
  const [dailySales, setDailySales] = useState([]);
  const [dailyPurchases, setDailyPurchases] = useState([]);

  // Raw data for chart drill-down
  const [rawSalesData, setRawSalesData] = useState([]);
  const [rawPurchasesData, setRawPurchasesData] = useState([]);

  // Chart drill-down modal
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [chartModalType, setChartModalType] = useState(''); // 'sale' or 'purchase'
  const [chartModalDate, setChartModalDate] = useState('');
  const [chartModalItems, setChartModalItems] = useState([]);

  // KPI Card Detail Modal
  const [kpiModalOpen, setKpiModalOpen] = useState(false);
  const [kpiModalTitle, setKpiModalTitle] = useState('');
  const [kpiModalData, setKpiModalData] = useState([]);
  const [kpiModalColumns, setKpiModalColumns] = useState([]);

  // Report data states
  const [customerDueData, setCustomerDueData] = useState([]);
  const [vendorDueData, setVendorDueData] = useState([]);
  const [stockData, setStockData] = useState([]);
  const [cashFlowData, setCashFlowData] = useState({ cashIn: 0, cashOut: 0, balance: 0 });

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('view');
  const [modalType, setModalType] = useState('');
  const [modalLoading, setModalLoading] = useState(false);

  // Purchase Return view modal states
  const [purchaseReturnViewModalOpen, setPurchaseReturnViewModalOpen] = useState(false);
  const [selectedPurchaseReturn, setSelectedPurchaseReturn] = useState(null);
  const [purchaseReturnDetails, setPurchaseReturnDetails] = useState([]);

  // Sale Return view modal states
  const [saleReturnViewModalOpen, setSaleReturnViewModalOpen] = useState(false);
  const [selectedSaleReturn, setSelectedSaleReturn] = useState(null);
  const [saleReturnDetails, setSaleReturnDetails] = useState([]);

  // Modal data states
  const [formData, setFormData] = useState({});
  const [detailsData, setDetailsData] = useState([]);
  const [relatedItems, setRelatedItems] = useState([]);

  // Master data for modals
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [banks, setBanks] = useState([]);
  const [masters, setMasters] = useState([]);
  const [freightPackingGST, setFreightPackingGST] = useState({ freight: null, packing: null });

  useEffect(() => {
    if (!validateSession()) {
      return;
    }
    fetchDashboardData();
  }, [validateSession]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
      const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];

      const [
        salesResponse,
        purchasesResponse,
        saleReturnsResponse,
        purchaseReturnsResponse,
        paymentReceiptsResponse,
        journalEntriesResponse,
        customerDueResponse,
        vendorDueResponse,
        stockResponse,
        cashBookResponse
      ] = await Promise.all([
        fetchData('SaleApi', `/ActiveList/${companyId}/${branchId || 1}/${branchId || 1}/${firstDayOfMonth}/${lastDayOfMonth}`, token).catch(() => ({ response_data: [] })),
        fetchData('PurchaseApi', `/ActiveList/${companyId}/${branchId || 1}/${firstDayOfMonth}/${lastDayOfMonth}`, token).catch(() => ({ response_data: [] })),
        fetchData('SaleReturnApi', `/ActiveList/${companyId}/${branchId || 1}/${firstDayOfMonth}/${lastDayOfMonth}`, token).catch(() => ({ response_data: [] })),
        fetchData('PurchaseReturnApi', `/ActiveList/${companyId}/${branchId || 1}/${firstDayOfMonth}/${lastDayOfMonth}`, token).catch(() => ({ response_data: [] })),
        fetchData('PaymentReceiptsApi', `/ActiveList/${companyId}/${branchId || 1}/${firstDayOfMonth}/${lastDayOfMonth}`, token).catch(() => ({ response_data: [] })),
        fetchData('JournalEntryApi', `/ActiveList/${companyId}/${branchId || 1}/${firstDayOfMonth}/${lastDayOfMonth}`, token).catch(() => ({ response_data: [] })),
        fetchData('ReportApi', `/CustomerDuelist/${today.toISOString().split('T')[0]}/${companyId}/0`, token).catch(() => ({ response_data: [] })),
        fetchData('ReportApi', `/VendorDuelist/${today.toISOString().split('T')[0]}/${companyId}/0`, token).catch(() => ({ response_data: [] })),
        fetchData('ReportApi', `/Stock/${companyId}/${branchId || 1}/0/${firstDayOfMonth}/${lastDayOfMonth}`, token).catch(() => ({ response_data: [] })),
        fetchData('ReportApi', `/CashBook/${companyId}/${branchId || 1}/${firstDayOfMonth}/${lastDayOfMonth}`, token).catch(() => ({ response_data: [] }))
      ]);

      const salesData = salesResponse.response_data || salesResponse || [];
      const purchasesData = purchasesResponse.response_data || purchasesResponse || [];
      const saleReturnsData = saleReturnsResponse.response_data || saleReturnsResponse || [];
      const purchaseReturnsData = purchaseReturnsResponse.response_data || purchaseReturnsResponse || [];
      const paymentReceiptsData = paymentReceiptsResponse.response_data || paymentReceiptsResponse || [];
      const journalEntriesData = journalEntriesResponse.response_data || journalEntriesResponse || [];

      // Calculate transaction stats
      setTransactionStats({
        sales: {
          count: salesData.length,
          amount: salesData.reduce((sum, item) => sum + (parseFloat(item.TotalAmount) || 0), 0)
        },
        purchases: {
          count: purchasesData.length,
          amount: purchasesData.reduce((sum, item) => sum + (parseFloat(item.TotalAmount) || 0), 0)
        },
        saleReturns: {
          count: saleReturnsData.length,
          amount: saleReturnsData.reduce((sum, item) => sum + (parseFloat(item.TotalAmount) || 0), 0)
        },
        purchaseReturns: {
          count: purchaseReturnsData.length,
          amount: purchaseReturnsData.reduce((sum, item) => sum + (parseFloat(item.TotalAmount) || 0), 0)
        },
        paymentReceipts: {
          count: paymentReceiptsData.length,
          amount: paymentReceiptsData.reduce((sum, item) => sum + (parseFloat(item.TotalAmount || item.Amount) || 0), 0)
        },
        journalEntries: {
          count: journalEntriesData.length,
          amount: journalEntriesData.reduce((sum, item) => sum + (parseFloat(item.TotalAmount || item.Amount) || 0), 0)
        }
      });

      // Build recent activities
      const activities = [];

      salesData.slice(0, 5).forEach(sale => {
        const totalAmt = parseFloat(sale.TotalAmount) || 0;
        const balanceAmt = parseFloat(sale.BalanceAmount) || 0;
        activities.push({
          type: 'sale',
          id: sale.Id,
          icon: <Receipt />,
          title: 'Sale Invoice',
          description: `${sale.BillNumber || sale.BillNo} - ${sale.MasterName || sale.CustomerName || 'Customer'}`,
          amount: totalAmt,
          balanceAmount: balanceAmt,
          status: balanceAmt <= 0 ? 'PAID' : balanceAmt < totalAmt ? 'PARTIAL' : 'PENDING',
          date: sale.BillDate || sale.CreatedDate,
          time: sale.CreatedTime || new Date(sale.CreatedDate || sale.BillDate).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          color: '#7c3aed'
        });
      });

      purchasesData.slice(0, 5).forEach(purchase => {
        const totalAmt = parseFloat(purchase.TotalAmount) || 0;
        const balanceAmt = parseFloat(purchase.BalanceAmount) || 0;
        activities.push({
          type: 'purchase',
          id: purchase.Id,
          icon: <ShoppingCart />,
          title: 'Purchase',
          description: `${purchase.BillNumber || purchase.BillNo} - ${purchase.MasterName || purchase.VendorName || 'Vendor'}`,
          amount: totalAmt,
          balanceAmount: balanceAmt,
          status: balanceAmt <= 0 ? 'PAID' : balanceAmt < totalAmt ? 'PARTIAL' : 'PENDING',
          date: purchase.BillDate || purchase.CreatedDate,
          time: purchase.CreatedTime || new Date(purchase.CreatedDate || purchase.BillDate).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          color: '#f59e0b'
        });
      });

      saleReturnsData.slice(0, 3).forEach(saleReturn => {
        const totalAmt = parseFloat(saleReturn.TotalAmount) || 0;
        const balanceAmt = parseFloat(saleReturn.BalanceAmount) || 0;
        activities.push({
          type: 'salereturn',
          id: saleReturn.Id,
          icon: <Undo />,
          title: 'Sale Return',
          description: `${saleReturn.BillNumber || saleReturn.BillNo} - ${saleReturn.MasterName || saleReturn.CustomerName || 'Customer'}`,
          amount: totalAmt,
          balanceAmount: balanceAmt,
          status: balanceAmt <= 0 ? 'PAID' : balanceAmt < totalAmt ? 'PARTIAL' : 'PENDING',
          date: saleReturn.BillDate || saleReturn.CreatedDate,
          time: saleReturn.CreatedTime || new Date(saleReturn.CreatedDate || saleReturn.BillDate).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          color: '#ef4444'
        });
      });

      purchaseReturnsData.slice(0, 3).forEach(purchaseReturn => {
        const totalAmt = parseFloat(purchaseReturn.TotalAmount) || 0;
        const balanceAmt = parseFloat(purchaseReturn.BalanceAmount) || 0;
        activities.push({
          type: 'purchasereturn',
          id: purchaseReturn.Id,
          icon: <Undo />,
          title: 'Purchase Return',
          description: `${purchaseReturn.BillNumber || purchaseReturn.BillNo} - ${purchaseReturn.MasterName || purchaseReturn.VendorName || 'Vendor'}`,
          amount: totalAmt,
          balanceAmount: balanceAmt,
          status: balanceAmt <= 0 ? 'PAID' : balanceAmt < totalAmt ? 'PARTIAL' : 'PENDING',
          date: purchaseReturn.BillDate || purchaseReturn.CreatedDate,
          time: purchaseReturn.CreatedTime || new Date(purchaseReturn.CreatedDate || purchaseReturn.BillDate).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          color: '#10b981'
        });
      });

      paymentReceiptsData.slice(0, 3).forEach(payment => {
        activities.push({
          type: 'payment',
          id: payment.Id,
          icon: <Payment />,
          title: 'Payment/Receipt',
          description: `${payment.BillNumber || payment.BillNo} - ${payment.MasterName || payment.PartyName || 'Party'}`,
          amount: parseFloat(payment.TotalAmount || payment.Amount) || 0,
          balanceAmount: 0,
          status: 'PAID',
          date: payment.BillDate || payment.CreatedDate,
          time: payment.CreatedTime || new Date(payment.CreatedDate || payment.BillDate).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          color: '#3b82f6'
        });
      });

      journalEntriesData.slice(0, 3).forEach(journal => {
        activities.push({
          type: 'journal',
          id: journal.Id,
          icon: <Description />,
          title: 'Journal Entry',
          description: `${journal.BillNumber || journal.BillNo} - ${journal.MasterName || journal.Narration || 'Entry'}`,
          amount: parseFloat(journal.TotalAmount || journal.Amount) || 0,
          balanceAmount: 0,
          status: 'POSTED',
          date: journal.BillDate || journal.CreatedDate,
          time: journal.CreatedTime || new Date(journal.CreatedDate || journal.BillDate).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          color: '#8b5cf6'
        });
      });

      activities.sort((a, b) => new Date(b.date) - new Date(a.date));
      setRecentActivities(activities.slice(0, 20));

      // Calculate daily sales for graph
      const salesByDay = {};
      const currentDate = new Date();
      const last30Days = [];

      // Initialize last 30 days
      for (let i = 29; i >= 0; i--) {
        const date = new Date(currentDate);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        last30Days.push(dateStr);
        salesByDay[dateStr] = 0;
      }

      // Aggregate sales by day
      salesData.forEach(sale => {
        const saleDate = new Date(sale.BillDate || sale.CreatedDate).toISOString().split('T')[0];
        if (salesByDay.hasOwnProperty(saleDate)) {
          salesByDay[saleDate] += parseFloat(sale.TotalAmount) || 0;
        }
      });

      // Convert to array for graph
      const dailySalesData = last30Days.map(date => ({
        date: date,
        amount: salesByDay[date],
        label: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      }));

      setDailySales(dailySalesData);
      setRawSalesData(salesData);

      // Calculate daily purchases for chart
      const purchasesByDay = {};
      last30Days.forEach(d => { purchasesByDay[d] = 0; });
      purchasesData.forEach(purchase => {
        const pDate = new Date(purchase.BillDate || purchase.CreatedDate).toISOString().split('T')[0];
        if (purchasesByDay.hasOwnProperty(pDate)) {
          purchasesByDay[pDate] += parseFloat(purchase.TotalAmount) || 0;
        }
      });
      const dailyPurchasesData = last30Days.map(date => ({
        date,
        amount: purchasesByDay[date],
        label: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      }));
      setDailyPurchases(dailyPurchasesData);
      setRawPurchasesData(purchasesData);

      // Process Customer Due (Receivable Aging)
      const custDueRaw = customerDueResponse?.response_data || customerDueResponse || [];
      setCustomerDueData(Array.isArray(custDueRaw) ? custDueRaw : []);

      // Process Vendor Due (Payable Aging)
      const vendDueRaw = vendorDueResponse?.response_data || vendorDueResponse || [];
      setVendorDueData(Array.isArray(vendDueRaw) ? vendDueRaw : []);

      // Process Stock Data
      const stockRaw = stockResponse?.response_data || stockResponse || [];
      setStockData(Array.isArray(stockRaw) ? stockRaw : []);

      // Process Cash Book Data
      const cashRaw = cashBookResponse?.response_data || cashBookResponse || [];
      if (Array.isArray(cashRaw)) {
        const cashIn = cashRaw.reduce((sum, item) => sum + (parseFloat(item.Debit || item.DebitAmount) || 0), 0);
        const cashOut = cashRaw.reduce((sum, item) => sum + (parseFloat(item.Credit || item.CreditAmount) || 0), 0);
        setCashFlowData({ cashIn, cashOut, balance: cashIn - cashOut });
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCompact = (amount) => {
    if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `${(amount / 1000).toFixed(1)}K`;
    return amount.toFixed(0);
  };

  // Calculate aging buckets for receivables
  const getReceivableAging = () => {
    const buckets = { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 };
    const total = customerDueData.reduce((sum, item) => sum + (parseFloat(item.BalanceAmount) || 0), 0);
    customerDueData.forEach(item => {
      const days = parseInt(item.DueDays) || 0;
      const amt = parseFloat(item.BalanceAmount) || 0;
      if (days <= 30) buckets['0-30'] += amt;
      else if (days <= 60) buckets['31-60'] += amt;
      else if (days <= 90) buckets['61-90'] += amt;
      else buckets['90+'] += amt;
    });
    return { buckets, total };
  };

  // Calculate aging buckets for payables
  const getPayableAging = () => {
    const buckets = { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 };
    const total = vendorDueData.reduce((sum, item) => sum + (parseFloat(item.BalanceAmount) || 0), 0);
    vendorDueData.forEach(item => {
      const days = parseInt(item.DueDays) || 0;
      const amt = parseFloat(item.BalanceAmount) || 0;
      if (days <= 30) buckets['0-30'] += amt;
      else if (days <= 60) buckets['31-60'] += amt;
      else if (days <= 90) buckets['61-90'] += amt;
      else buckets['90+'] += amt;
    });
    return { buckets, total };
  };

  const getStockSummary = () => {
    const totalItems = stockData.length;
    const totalValue = stockData.reduce((sum, item) => sum + (parseFloat(item.StockValue || item.Amount || 0) * (parseFloat(item.ClosingQty || item.Quantity || 0))), 0);
    const lowStock = stockData.filter(item => (parseFloat(item.ClosingQty || item.Quantity || 0)) <= 0).length;
    return { totalItems, totalValue, lowStock };
  };

  const handleCardClick = (type) => {
    const routes = {
      sale: '/SaleIndex',
      purchase: '/PurchaseIndex',
      salereturn: '/SaleReturnAgainstIndex',
      purchasereturn: '/PurchaseReturnAgainstIndex',
      payment: '/PaymentAndReceiptsIndex',
      journal: '/JournalEntryIndex'
    };
    history.push(routes[type]);
  };

  const handleChartBarClick = (date, type) => {
    const dateStr = date;
    let filtered = [];
    if (type === 'sale') {
      filtered = rawSalesData.filter(item => {
        const itemDate = new Date(item.BillDate || item.CreatedDate).toISOString().split('T')[0];
        return itemDate === dateStr;
      });
    } else {
      filtered = rawPurchasesData.filter(item => {
        const itemDate = new Date(item.BillDate || item.CreatedDate).toISOString().split('T')[0];
        return itemDate === dateStr;
      });
    }
    setChartModalType(type);
    setChartModalDate(dateStr);
    setChartModalItems(filtered);
    setChartModalOpen(true);
  };

  // Handle KPI Card label clicks to show filtered data in modal
  const handleKpiLabelClick = (e, type, filter) => {
    e.stopPropagation(); // Prevent card's onClick from firing

    let title = '';
    let data = [];
    let columns = [];

    if (type === 'receivable') {
      columns = [
        { key: 'index', label: '#', align: 'left' },
        { key: 'CustomerName', label: 'Customer', align: 'left' },
        { key: 'BillNumber', label: 'Bill No', align: 'left' },
        { key: 'BillDate', label: 'Bill Date', align: 'left', format: 'date' },
        { key: 'CurrencyName', label: 'Currency', align: 'center' },
        { key: 'DueDays', label: 'Due Days', align: 'center' },
        { key: 'SaleAmount', label: 'Sale Amount', align: 'right', format: 'currency' },
        { key: 'PaidAmount', label: 'Paid', align: 'right', format: 'currency' },
        { key: 'BalanceAmount', label: 'Balance', align: 'right', format: 'currency' }
      ];

      // Enrich customerDueData with CurrencyName from rawSalesData
      const salesCurrencyMap = {};
      rawSalesData.forEach(s => {
        const key = s.BillNumber || s.BillNo;
        if (key) salesCurrencyMap[key] = s.CurrencyName || '';
      });
      const enrichedCustomerData = customerDueData.map(item => ({
        ...item,
        CurrencyName: item.CurrencyName || salesCurrencyMap[item.BillNumber] || ''
      }));

      if (filter === 'overdue60') {
        title = 'Accounts Receivable — Overdue (60+ Days)';
        data = enrichedCustomerData.filter(i => (parseInt(i.DueDays) || 0) > 60);
      } else if (filter === 'creditClients') {
        title = 'Accounts Receivable — Credit Clients';
        data = enrichedCustomerData;
      } else if (filter === 'due030') {
        title = 'Accounts Receivable — Due (0-30 Days)';
        data = enrichedCustomerData.filter(i => (parseInt(i.DueDays) || 0) <= 30);
      } else if (filter === 'all') {
        title = 'Accounts Receivable — All Bills';
        data = enrichedCustomerData;
      }
    } else if (type === 'payable') {
      columns = [
        { key: 'index', label: '#', align: 'left' },
        { key: 'SupplierName', label: 'Vendor', align: 'left', fallback: 'VendorName' },
        { key: 'BillNumber', label: 'Bill No', align: 'left' },
        { key: 'BillDate', label: 'Bill Date', align: 'left', format: 'date' },
        { key: 'CurrencyName', label: 'Currency', align: 'center' },
        { key: 'DueDays', label: 'Due Days', align: 'center' },
        { key: 'PurchaseAmount', label: 'Purchase Amount', align: 'right', format: 'currency' },
        { key: 'PaidAmount', label: 'Paid', align: 'right', format: 'currency' },
        { key: 'BalanceAmount', label: 'Balance', align: 'right', format: 'currency' }
      ];

      // Enrich vendorDueData with CurrencyName from rawPurchasesData
      const purchaseCurrencyMap = {};
      rawPurchasesData.forEach(p => {
        const key = p.BillNumber || p.BillNo;
        if (key) purchaseCurrencyMap[key] = p.CurrencyName || '';
      });
      const enrichedVendorData = vendorDueData.map(item => ({
        ...item,
        CurrencyName: item.CurrencyName || purchaseCurrencyMap[item.BillNumber] || ''
      }));

      if (filter === 'overdue60') {
        title = 'Accounts Payable — Overdue (60+ Days)';
        data = enrichedVendorData.filter(i => (parseInt(i.DueDays) || 0) > 60);
      } else if (filter === 'activeVendors') {
        title = 'Accounts Payable — Active Vendors';
        data = enrichedVendorData;
      } else if (filter === 'due030') {
        title = 'Accounts Payable — Due (0-30 Days)';
        data = enrichedVendorData.filter(i => (parseInt(i.DueDays) || 0) <= 30);
      } else if (filter === 'all') {
        title = 'Accounts Payable — All Bills';
        data = enrichedVendorData;
      }
    } else if (type === 'revenue') {
      columns = [
        { key: 'index', label: '#', align: 'left' },
        { key: 'MasterName', label: 'Customer', align: 'left', fallback: 'CustomerName' },
        { key: 'BillNumber', label: 'Bill No', align: 'left', fallback: 'BillNo' },
        { key: 'BillDate', label: 'Bill Date', align: 'left', format: 'date' },
        { key: 'CurrencyName', label: 'Currency', align: 'center' },
        { key: 'TotalAmount', label: 'Amount', align: 'right', format: 'currency' },
        { key: 'BalanceAmount', label: 'Balance', align: 'right', format: 'currency' }
      ];

      if (filter === 'todaySales') {
        title = 'Revenue — Today\'s Sales';
        const today = new Date().toISOString().split('T')[0];
        data = rawSalesData.filter(item => {
          const itemDate = new Date(item.BillDate || item.CreatedDate).toISOString().split('T')[0];
          return itemDate === today;
        });
      } else if (filter === 'totalBills') {
        title = 'Revenue — All Bills (This Month)';
        data = rawSalesData;
      } else if (filter === 'avgBill') {
        title = 'Revenue — All Bills (This Month)';
        data = rawSalesData;
      } else if (filter === 'all') {
        title = 'Revenue — All Sales (This Month)';
        data = rawSalesData;
      }
    } else if (type === 'purchases') {
      columns = [
        { key: 'index', label: '#', align: 'left' },
        { key: 'MasterName', label: 'Vendor', align: 'left', fallback: 'VendorName' },
        { key: 'BillNumber', label: 'Bill No', align: 'left', fallback: 'BillNo' },
        { key: 'BillDate', label: 'Bill Date', align: 'left', format: 'date' },
        { key: 'CurrencyName', label: 'Currency', align: 'center' },
        { key: 'TotalAmount', label: 'Amount', align: 'right', format: 'currency' },
        { key: 'BalanceAmount', label: 'Balance', align: 'right', format: 'currency' }
      ];

      if (filter === 'todayPurchase') {
        title = 'Purchases — Today\'s Purchases';
        const today = new Date().toISOString().split('T')[0];
        data = rawPurchasesData.filter(item => {
          const itemDate = new Date(item.BillDate || item.CreatedDate).toISOString().split('T')[0];
          return itemDate === today;
        });
      } else if (filter === 'totalBills') {
        title = 'Purchases — All Bills (This Month)';
        data = rawPurchasesData;
      } else if (filter === 'all') {
        title = 'Purchases — All Purchases (This Month)';
        data = rawPurchasesData;
      }
    } else if (type === 'stock') {
      columns = [
        { key: 'index', label: '#', align: 'left' },
        { key: 'ItemName', label: 'Item Name', align: 'left', fallback: 'Name' },
        { key: 'HSNCode', label: 'HSN Code', align: 'left' },
        { key: 'ClosingQty', label: 'Quantity', align: 'center', fallback: 'Quantity' },
        { key: 'StockValue', label: 'Value', align: 'right', format: 'currency', fallback: 'Amount' }
      ];

      if (filter === 'lowStock') {
        title = 'Stock — Low Stock Items';
        data = stockData.filter(item => (parseFloat(item.ClosingQty || item.Quantity || 0)) <= 0);
      } else if (filter === 'costOfStock') {
        title = 'Stock — All Items';
        data = stockData;
      } else if (filter === 'totalSkus') {
        title = 'Stock — All SKUs';
        data = stockData;
      } else if (filter === 'all') {
        title = 'Stock — All Items';
        data = stockData;
      }
    } else if (type === 'cashflow') {
      // Cash flow doesn't have detail rows, show summary
      title = 'Net Cash Flow — Summary';
      columns = [
        { key: 'index', label: '#', align: 'left' },
        { key: 'label', label: 'Description', align: 'left' },
        { key: 'amount', label: 'Amount', align: 'right', format: 'currency' }
      ];
      data = [
        { label: 'Cash In', amount: cashFlowData.cashIn },
        { label: 'Cash Out', amount: cashFlowData.cashOut },
        { label: 'Net Balance', amount: cashFlowData.balance }
      ];
    }

    setKpiModalTitle(title);
    setKpiModalData(data);
    setKpiModalColumns(columns);
    setKpiModalOpen(true);
  };

  const fetchMasterData = async (type) => {
    try {
      if (type === 'sale' || type === 'salereturn') {
        // Fetch customers and items for sale
        const [customersRes, itemsRes, banksRes, freightRes] = await Promise.all([
          fetchData('ClientAndVendorApi', `/ClientList/${companyId}`, token).catch(() => ({ response_data: [] })),
          fetchData('SaleApi', `/ItemStockListDetails/${companyId}/${branchId}`, token).catch(() => ({ response_data: [] })),
          fetchData('BankApi', `/${companyId}`, token).catch(() => ({ response_data: [] })),
          fetchData('SaleOrderApi', `/AccountHeadFreightAndPacking/${companyId}/${branchId || 1}`, token).catch(() => ({ response_data: [] }))
        ]);

        setCustomers(customersRes.response_data || customersRes || []);
        setItems(itemsRes.response_data || itemsRes || []);
        setBanks(banksRes.response_data || banksRes || []);

        const freightData = (freightRes.response_data || freightRes || []);
        const freight = Array.isArray(freightData) ? freightData.find(item => item.Name?.toLowerCase().includes('freight')) : null;
        const packing = Array.isArray(freightData) ? freightData.find(item => item.Name?.toLowerCase().includes('packing')) : null;
        setFreightPackingGST({ freight, packing });

      } else if (type === 'purchase' || type === 'purchasereturn') {
        // Fetch vendors and items for purchase
        const [vendorsRes, itemsRes, freightRes] = await Promise.all([
          fetchData('ClientAndVendorApi', `/VendorList/${companyId}`, token).catch(() => ({ response_data: [] })),
          fetchData('PurchaseApi', `/ItemStockListDetails/${companyId}/${branchId}`, token).catch(() => ({ response_data: [] })),
          fetchData('PurchaseOrderApi', `/AccountHeadFreightAndPacking/${companyId}/${branchId || 1}`, token).catch(() => ({ response_data: [] }))
        ]);

        setVendors(vendorsRes.response_data || vendorsRes || []);
        setItems(itemsRes.response_data || itemsRes || []);

        const freightData = (freightRes.response_data || freightRes || []);
        const freight = Array.isArray(freightData) ? freightData.find(item => item.Name?.toLowerCase().includes('freight')) : null;
        const packing = Array.isArray(freightData) ? freightData.find(item => item.Name?.toLowerCase().includes('packing')) : null;
        setFreightPackingGST({ freight, packing });

      } else if (type === 'payment') {
        // Fetch masters and banks for payment/receipt
        const [mastersRes, banksRes] = await Promise.all([
          fetchData('PaymentAndReceiptApi', `/Master/${companyId}`, token).catch(() => ({ response_data: [] })),
          fetchData('BankApi', `/${companyId}`, token).catch(() => ({ response_data: [] }))
        ]);

        setMasters(mastersRes.response_data || mastersRes || []);
        setBanks(banksRes.response_data || banksRes || []);

      } else if (type === 'journal') {
        // Fetch masters for journal entry
        const mastersRes = await fetchData('JournalEntryApi', `/Master/${companyId}`, token).catch(() => ({ response_data: [] }));
        setMasters(mastersRes.response_data || mastersRes || []);
      }
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const handleActivityClick = async (activity) => {
    setModalLoading(true);
    setModalType(activity.type);
    setModalAction('view');

    try {
      // Fetch master data first
      await fetchMasterData(activity.type);

      // Fetch specific record details
      let response;
      const apiMap = {
        sale: { api: 'SaleApi', endpoint: `/${activity.id}` },
        purchase: { api: 'PurchaseApi', endpoint: `/${activity.id}` },
        salereturn: { api: 'SaleReturnApi', endpoint: `/${activity.id}` },
        purchasereturn: { api: 'PurchaseReturnApi', endpoint: `/${activity.id}` },
        payment: { api: 'PaymentAndReceiptApi', endpoint: `/PaymentData/${activity.id}` },
        journal: { api: 'JournalEntryApi', endpoint: `/${activity.id}` }
      };

      const apiInfo = apiMap[activity.type];
      if (apiInfo) {
        response = await fetchData(apiInfo.api, apiInfo.endpoint, token);
        const data = response.response_data || response;
        
        // Format data based on type
        if (activity.type === 'sale') {
          const saleData = data.Sale || data;
          const details = data.SaleDetails || [];
          const productRelationDetails = data.SaleProductRelationDetails || [];

          setFormData({
            Id: saleData.Id,
            CompanyId: saleData.CompanyId || companyId,
            BranchId: saleData.BranchId || branchId,
            MasterId: saleData.MasterId,
            CustomerName: saleData.CustomerName || '',
            VATNumber: saleData.GSTNumber || saleData.VATNumber || '',
            SalesManId: saleData.SalesManId || null,
            StateId: saleData.StateId || null,
            BillNumber: saleData.BillNumber,
            BillPrefix: saleData.BillPrefix || '',
            FinYear: saleData.FinYear || '',
            BillDate: saleData.BillDate?.split('T')[0] || new Date().toISOString().split('T')[0],
            ClearingDate: saleData.ClearingDate?.split('T')[0] || new Date().toISOString().split('T')[0],
            GstCalculationType: saleData.GstCalculationType || 1,
            GstCalculationTypeName: saleData.GstCalculationTypeName || 'Excluding Tax',
            Amount: saleData.Amount || 0,
            DiscountAmount: saleData.DiscountAmount || 0,
            TaxableAmount: saleData.TaxableAmount || 0,
            VATAmount: saleData.VATAmount || 0,
            FreightAmount: saleData.FreightAmount || 0,
            FreightVATRate: saleData.FreightVATRate || 0,
            FreightVATAmount: saleData.FreightVATAmount || 0,
            PackingAmount: saleData.PackingAmount || 0,
            PackingVATRate: saleData.PackingVATRate || 0,
            PackingVATAmount: saleData.PackingVATAmount || 0,
            RoundOff: saleData.RoundOff || 0,
            TotalAmount: saleData.TotalAmount || saleData.GrandTotal || 0,
            GrandTotal: saleData.GrandTotal || saleData.TotalAmount || 0,
            CashAmount: saleData.CashAmount || 0,
            UPIAmount: saleData.UPIAmount || 0,
            CardAmount: saleData.CardAmount || 0,
            BankId: saleData.BankId || null,
            PaidAmount: saleData.PaidAmount || 0,
            BalanceAmount: saleData.BalanceAmount || 0,
            Remarks: saleData.Remarks || '',
            DispatchFrom: saleData.DispatchFrom || '',
            DispatchAddress1: saleData.DispatchAddress1 || '',
            DispatchAddress2: saleData.DispatchAddress2 || '',
            DispatchLocation: saleData.DispatchLocation || '',
            DispatchPincode: saleData.DispatchPincode || '',
            ShipTo: saleData.ShipTo || '',
            ShipGSTNo: saleData.ShipGSTNo || '',
            ShipToAddress1: saleData.ShipToAddress1 || '',
            ShipToAddress2: saleData.ShipToAddress2 || '',
            ShipToLocation: saleData.ShipToLocation || '',
            ShipToPincode: saleData.ShipToPincode || '',
            VehicleNumber: saleData.VehicleNumber || '',
            TransporterName: saleData.TransporterName || '',
            TransporterVAT: saleData.TransporterVAT || '',
            Distance: saleData.Distance || 0,
            IsVerified: saleData.IsVerified || false,
            IsActive: saleData.IsActive ?? true,
            CreatedBy: saleData.CreatedBy,
            CreatedDate: saleData.CreatedDate,
            CreatedByName: saleData.CreatedByName,
            ModifiedBy: saleData.ModifiedBy,
            ModifiedDate: saleData.ModifiedDate,
            ModifiedByName: saleData.ModifiedByName
          });

          setDetailsData(details.map(d => {
            const quantity = d.Quantity || 0;
            const rate = d.Rate || 0;
            const calculatedAmount = quantity * rate;

            return {
              Id: d.Id || 0,
              ItemId: d.ItemId,
              ItemName: d.ItemName || '',
              HSNCode: d.HSNCode || '',
              VATRate: d.VATRate || d.GSTRate || 0,
              GSTRate: d.GSTRate || 0,
              Quantity: quantity,
              Rate: rate,
              Amount: d.Amount || calculatedAmount,
              DiscountPercentage: d.DiscountPercentage || 0,
              DiscountAmount: d.DiscountAmount || 0,
              TaxableAmount: d.TaxableAmount || 0,
              VATAmount: d.VATAmount || 0,
              TotalAmount: d.TotalAmount || 0,
              IsActive: d.IsActive ?? true,
              CreatedBy: d.CreatedBy,
              CreatedDate: d.CreatedDate,
              ModifiedBy: d.ModifiedBy,
              ModifiedDate: d.ModifiedDate
            };
          }));

          // Transform product relation details into relatedItems structure
          const relatedItemsMap = {};
          productRelationDetails.forEach(rel => {
            const key = `${rel.ProductItemId}_${rel.SaleDetailId}`;
            if (!relatedItemsMap[key]) {
              relatedItemsMap[key] = {
                productId: rel.ProductItemId,
                productName: rel.ProductItemName || 'Product',
                productQuantity: rel.ProductQuantity || 1,
                saleDetailId: rel.SaleDetailId,
                items: []
              };
            }
            relatedItemsMap[key].items.push({
              Id: rel.Id,
              ItemId: rel.RelatedItemId,
              ItemName: rel.RelatedItemName || '',
              Quantity: rel.Quantity || 0,
              Qty: rel.Quantity || 0,
              Rate: rel.Rate || 0,
              SaleRate: rel.Rate || 0,
              Amount: (rel.Quantity || 0) * (rel.Rate || 0)
            });
          });

          setRelatedItems(Object.values(relatedItemsMap));

        } else if (activity.type === 'purchase') {
          const purchaseData = data.Purchase || data;
          const details = data.PurchaseDetails || [];

          setFormData({
            Id: purchaseData.Id,
            CompanyId: purchaseData.CompanyId || companyId,
            BranchId: purchaseData.BranchId || branchId,
            MasterId: purchaseData.MasterId,
            MasterName: purchaseData.MasterName || purchaseData.VendorName || '',
            InvoiceTypeId: purchaseData.InvoiceTypeId || 0,
            InvoiceTypeName: purchaseData.InvoiceTypeName || '',
            VATNumber: purchaseData.VATNumber || purchaseData.GSTNumber || '',
            VATCalculationType: purchaseData.VATCalculationType || 1,
            VATCalculationTypeName: purchaseData.VATCalculationTypeName || 'Excluding Tax',
            PurchaseOrderId: purchaseData.PurchaseOrderId || null,
            PurchaseOrderNo: purchaseData.PurchaseOrderNo || '',
            PurchaseOrderDate: purchaseData.PurchaseOrderDate?.split('T')[0] || '',
            BillDate: purchaseData.BillDate?.split('T')[0] || new Date().toISOString().split('T')[0],
            ClearingDate: purchaseData.ClearingDate?.split('T')[0] || '',
            BillNumber: purchaseData.BillNumber || '',
            TotalQuantity: purchaseData.TotalQuantity || 0,
            Amount: purchaseData.Amount || 0,
            VATAmount: purchaseData.VATAmount || 0,
            TaxableAmount: purchaseData.TaxableAmount || 0,
            FreightAmount: purchaseData.FreightAmount || 0,
            FreightVATRate: purchaseData.FreightVATRate || purchaseData.FreightGSTRate || 0,
            FreightVATAmount: purchaseData.FreightVATAmount || purchaseData.FreightGSTAmount || 0,
            PackingAmount: purchaseData.PackingAmount || 0,
            PackingVATRate: purchaseData.PackingVATRate || purchaseData.PackingGSTRate || 0,
            PackingVATAmount: purchaseData.PackingVATAmount || purchaseData.PackingGSTAmount || 0,
            TCSAmount: purchaseData.TCSAmount || 0,
            DiscountAmount: purchaseData.DiscountAmount || 0,
            RoundOff: purchaseData.RoundOff || 0,
            TotalAmount: purchaseData.TotalAmount || 0,
            PaidAmount: purchaseData.PaidAmount || 0,
            BalanceAmount: purchaseData.BalanceAmount || 0,
            Remarks: purchaseData.Remarks || '',
            IsActive: purchaseData.IsActive ?? true,
            IsVerified: purchaseData.IsVerified ?? false,
            IsConvertToGoodReceipt: purchaseData.IsConvertToGoodReceipt ?? false,
            CreatedBy: purchaseData.CreatedBy,
            CreatedByName: purchaseData.CreatedByName || '',
            CreatedDate: purchaseData.CreatedDate,
            ModifiedBy: purchaseData.ModifiedBy,
            ModifiedByName: purchaseData.ModifiedByName || '',
            ModifiedDate: purchaseData.ModifiedDate
          });

          setDetailsData(details.map(d => ({
            Id: d.Id || 0,
            PurchaseId: d.PurchaseId || 0,
            CompanyId: d.CompanyId || companyId,
            BranchId: d.BranchId || branchId,
            ItemId: d.ItemId,
            ItemName: d.ItemName || '',
            BatchNumber: d.BatchNumber || '',
            PurchaseOrderId: d.PurchaseOrderId || null,
            OrderQuantity: d.OrderQuantity || 0,
            ReceivedQuantity: d.ReceivedQuantity || 0,
            BilledQuantity: d.BilledQuantity || 0,
            DamageQuantity: d.DamageQuantity || 0,
            ExcessQuantity: d.ExcessQuantity || 0,
            LessQuantity: d.LessQuantity || 0,
            StockQuantity: d.StockQuantity || 0,
            ReturnDamageQuantity: d.ReturnDamageQuantity || 0,
            ReturnStockQuantity: d.ReturnStockQuantity || 0,
            Rate: d.Rate || 0,
            Amount: d.Amount || 0,
            DiscountPercentage: d.DiscountPercentage || 0,
            DiscountAmount: d.DiscountAmount || 0,
            HSNCode: d.HSNCode || '',
            VATRate: d.VATRate || d.GSTRate || 0,
            TaxableAmount: d.TaxableAmount || 0,
            VATAmount: d.VATAmount || 0,
            TotalAmount: d.TotalAmount || 0,
            IsActive: d.IsActive ?? true,
            CreatedBy: d.CreatedBy,
            CreatedDate: d.CreatedDate,
            ModifiedBy: d.ModifiedBy,
            ModifiedDate: d.ModifiedDate
          })));

        } else if (activity.type === 'purchasereturn') {
          const returnData = data.PurchaseReturn || data;
          const details = data.PurchaseReturnDetails || [];

          setSelectedPurchaseReturn(returnData);
          setPurchaseReturnDetails(details);
          setPurchaseReturnViewModalOpen(true);

        } else if (activity.type === 'salereturn') {
          const returnData = data.SaleReturn || data;
          const details = data.SaleReturnDetails || [];

          // Ensure all required fields are present
          const completeReturnData = {
            ...returnData,
            MasterName: returnData.MasterName || returnData.CustomerName || '',
            CustomerName: returnData.CustomerName || returnData.MasterName || '',
            VATNumber: returnData.VATNumber || returnData.GSTNumber || '',
            InvoiceTypeName: returnData.InvoiceTypeName || '',
            BillNumber: returnData.BillNumber || '',
            BillDate: returnData.BillDate || '',
            SaleNo: returnData.SaleNo || '',
            VATCalculationType: returnData.VATCalculationType || 1,
            FreightAmount: returnData.FreightAmount || 0,
            PackingAmount: returnData.PackingAmount || 0,
            TCSAmount: returnData.TCSAmount || 0,
            CashAmount: returnData.CashAmount || 0,
            UPIAmount: returnData.UPIAmount || 0,
            CardAmount: returnData.CardAmount || 0,
            BankId: returnData.BankId || '',
            Remarks: returnData.Remarks || ''
          };

          setSelectedSaleReturn(completeReturnData);
          setSaleReturnDetails(details);
          setSaleReturnViewModalOpen(true);

        } else if (activity.type === 'payment') {
          const paymentData = data.PaymentAndReceipt || data;

          setFormData({
            Id: paymentData.Id,
            CompanyId: paymentData.CompanyId || companyId,
            BranchId: paymentData.BranchId || branchId,
            MasterId: paymentData.MasterId,
            BillNumber: paymentData.BillNumber,
            BillDate: paymentData.BillDate?.split('T')[0] || new Date().toISOString().split('T')[0],
            IsType: paymentData.IsType,
            TotalAmount: paymentData.TotalAmount || paymentData.Amount || 0,
            Remarks: paymentData.Remarks || ''
          });

          setDetailsData([]);

        } else if (activity.type === 'journal') {
          const journalData = data.JournalEntry || data;
          const details = data.JournalEntryDetails || [];

          // Ensure all required fields are present
          const completeJournalData = {
            Id: journalData.Id,
            CompanyId: journalData.CompanyId || companyId,
            BranchId: journalData.BranchId || branchId,
            BillNumber: journalData.BillNumber || '',
            BillPrefix: journalData.BillPrefix || '',
            FinYear: journalData.FinYear || '',
            BillDate: journalData.BillDate?.split('T')[0] || new Date().toISOString().split('T')[0],
            Narration: journalData.Narration || '',
            TotalAmount: journalData.TotalAmount || journalData.Amount || 0,
            TotalChildAmount: journalData.TotalChildAmount || 0,
            IsActive: journalData.IsActive ?? true,
            CreatedBy: journalData.CreatedBy,
            CreatedDate: journalData.CreatedDate,
            CreatedByName: journalData.CreatedByName || '',
            ModifiedBy: journalData.ModifiedBy,
            ModifiedDate: journalData.ModifiedDate,
            ModifiedByName: journalData.ModifiedByName || ''
          };

          // Map journal entry details with complete fields
          const completeDetails = details.map(detail => ({
            Id: detail.Id || 0,
            JournalEntryId: detail.JournalEntryId || journalData.Id,
            MasterId: detail.MasterId || detail.AccountHeadId || 0,
            MasterName: detail.MasterName || detail.AccountHeadName || '',
            Amount: detail.Amount || 0,
            IsDebit: detail.IsDebit ?? true,
            Remarks: detail.Remarks || '',
            IsActive: detail.IsActive ?? true,
            CreatedBy: detail.CreatedBy,
            CreatedDate: detail.CreatedDate,
            ModifiedBy: detail.ModifiedBy,
            ModifiedDate: detail.ModifiedDate
          }));

          setFormData(completeJournalData);
          setDetailsData(completeDetails);
        }

        setOpenModal(true);
      }
    } catch (error) {
      console.error('Error fetching activity details:', error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalType('');
    setFormData({});
    setDetailsData([]);
    setRelatedItems([]);
  };

  const handleSave = () => {
    // Not needed for view mode, but required prop
    handleCloseModal();
  };

  if (loading) {
    return <DashboardLoader />;
  }

  const receivableAging = getReceivableAging();
  const payableAging = getPayableAging();
  const stockSummary = getStockSummary();
  const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="erp-dashboard">
      {/* TOP KPI ROW */}
      <div className="kpi-row">
        {/* Accounts Receivable */}
        <div className="kpi-card kpi-receivable" onClick={() => history.push('/CustomerAging')}>
          <div className="kpi-card-top">
            <div className="kpi-card-icon"><Receipt /></div>
            <span className="kpi-card-label">Accounts Receivable</span>
            <span className="kpi-card-badge">{customerDueData.length} bills</span>
          </div>
          <p className="kpi-card-amount">{formatCurrency(receivableAging.total)}</p>
          <div className="kpi-card-details">
            <div className="kpi-detail-row kpi-detail-clickable" onClick={(e) => handleKpiLabelClick(e, 'receivable', 'overdue60')}><span className="kpi-detail-label">Overdue (60+d)</span><span className="kpi-detail-value">{customerDueData.filter(i => (parseInt(i.DueDays)||0) > 60).length}</span></div>
            <div className="kpi-detail-row kpi-detail-clickable" onClick={(e) => handleKpiLabelClick(e, 'receivable', 'creditClients')}><span className="kpi-detail-label">Credit Clients</span><span className="kpi-detail-value">{new Set(customerDueData.map(i => i.CustomerName)).size}</span></div>
            <div className="kpi-detail-row kpi-detail-clickable" onClick={(e) => handleKpiLabelClick(e, 'receivable', 'due030')}><span className="kpi-detail-label">Due (0-30d)</span><span className="kpi-detail-value">{formatCurrency(receivableAging.buckets['0-30'])}</span></div>
          </div>
        </div>

        {/* Accounts Payable */}
        <div className="kpi-card kpi-payable" onClick={() => history.push('/VendorAging')}>
          <div className="kpi-card-top">
            <div className="kpi-card-icon"><ShoppingCart /></div>
            <span className="kpi-card-label">Accounts Payable</span>
            <span className="kpi-card-badge">{vendorDueData.length} bills</span>
          </div>
          <p className="kpi-card-amount">{formatCurrency(payableAging.total)}</p>
          <div className="kpi-card-details">
            <div className="kpi-detail-row kpi-detail-clickable" onClick={(e) => handleKpiLabelClick(e, 'payable', 'overdue60')}><span className="kpi-detail-label">Overdue (60+d)</span><span className="kpi-detail-value">{vendorDueData.filter(i => (parseInt(i.DueDays)||0) > 60).length}</span></div>
            <div className="kpi-detail-row kpi-detail-clickable" onClick={(e) => handleKpiLabelClick(e, 'payable', 'activeVendors')}><span className="kpi-detail-label">Active Vendors</span><span className="kpi-detail-value">{new Set(vendorDueData.map(i => i.SupplierName || i.VendorName)).size}</span></div>
            <div className="kpi-detail-row kpi-detail-clickable" onClick={(e) => handleKpiLabelClick(e, 'payable', 'due030')}><span className="kpi-detail-label">Due (0-30d)</span><span className="kpi-detail-value">{formatCurrency(payableAging.buckets['0-30'])}</span></div>
          </div>
        </div>

        {/* Revenue */}
        <div className="kpi-card kpi-revenue" onClick={() => handleCardClick('sale')}>
          <div className="kpi-card-top">
            <div className="kpi-card-icon"><TrendingUp /></div>
            <span className="kpi-card-label">Revenue (Month)</span>
            <span className="kpi-card-badge">{transactionStats.sales.count} bills</span>
          </div>
          <p className="kpi-card-amount">{formatCurrency(transactionStats.sales.amount)}</p>
          <div className="kpi-card-details">
            <div className="kpi-detail-row kpi-detail-clickable" onClick={(e) => handleKpiLabelClick(e, 'revenue', 'todaySales')}><span className="kpi-detail-label">Today's Sales</span><span className="kpi-detail-value">{dailySales.length > 0 ? formatCurrency(dailySales[dailySales.length - 1]?.amount || 0) : '0'}</span></div>
            <div className="kpi-detail-row kpi-detail-clickable" onClick={(e) => handleKpiLabelClick(e, 'revenue', 'totalBills')}><span className="kpi-detail-label">Total Bills</span><span className="kpi-detail-value">{transactionStats.sales.count}</span></div>
            <div className="kpi-detail-row kpi-detail-clickable" onClick={(e) => handleKpiLabelClick(e, 'revenue', 'avgBill')}><span className="kpi-detail-label">Avg Bill</span><span className="kpi-detail-value">{formatCurrency(transactionStats.sales.count > 0 ? transactionStats.sales.amount / transactionStats.sales.count : 0)}</span></div>
          </div>
        </div>

        {/* Purchases */}
        <div className="kpi-card kpi-purchases" onClick={() => handleCardClick('purchase')}>
          <div className="kpi-card-top">
            <div className="kpi-card-icon"><ShoppingCart /></div>
            <span className="kpi-card-label">Purchases (Month)</span>
            <span className="kpi-card-badge">{transactionStats.purchases.count} bills</span>
          </div>
          <p className="kpi-card-amount">{formatCurrency(transactionStats.purchases.amount)}</p>
          <div className="kpi-card-details">
            <div className="kpi-detail-row kpi-detail-clickable" onClick={(e) => handleKpiLabelClick(e, 'purchases', 'todayPurchase')}><span className="kpi-detail-label">Today's Purchase</span><span className="kpi-detail-value">{dailyPurchases.length > 0 ? formatCurrency(dailyPurchases[dailyPurchases.length - 1]?.amount || 0) : '0'}</span></div>
            <div className="kpi-detail-row kpi-detail-clickable" onClick={(e) => handleKpiLabelClick(e, 'purchases', 'totalBills')}><span className="kpi-detail-label">Total Bills</span><span className="kpi-detail-value">{transactionStats.purchases.count}</span></div>
            <div className="kpi-detail-row"><span className="kpi-detail-label">Pending POs</span><span className="kpi-detail-value">-</span></div>
          </div>
        </div>

        {/* Stock Value */}
        <div className="kpi-card kpi-stock" onClick={() => history.push('/StockReport')}>
          <div className="kpi-card-top">
            <div className="kpi-card-icon"><Inventory /></div>
            <span className="kpi-card-label">Stock Value</span>
            <span className="kpi-card-badge">{stockSummary.totalItems} items</span>
          </div>
          <p className="kpi-card-amount">{formatCurrency(stockSummary.totalValue)}</p>
          <div className="kpi-card-details">
            <div className="kpi-detail-row kpi-detail-clickable" onClick={(e) => handleKpiLabelClick(e, 'stock', 'lowStock')}><span className="kpi-detail-label">Low Stock Items</span><span className="kpi-detail-value">{stockSummary.lowStock}</span></div>
            <div className="kpi-detail-row kpi-detail-clickable" onClick={(e) => handleKpiLabelClick(e, 'stock', 'costOfStock')}><span className="kpi-detail-label">Cost of Stock</span><span className="kpi-detail-value">{formatCompact(stockSummary.totalValue)}</span></div>
            <div className="kpi-detail-row kpi-detail-clickable" onClick={(e) => handleKpiLabelClick(e, 'stock', 'totalSkus')}><span className="kpi-detail-label">Total SKUs</span><span className="kpi-detail-value">{stockSummary.totalItems}</span></div>
          </div>
        </div>

        {/* Net Cash Flow */}
        <div className="kpi-card kpi-cashflow" onClick={() => history.push('/CashBook')}>
          <div className="kpi-card-top">
            <div className="kpi-card-icon"><AccountBalanceWallet /></div>
            <span className="kpi-card-label">Net Cash Flow</span>
          </div>
          <p className="kpi-card-amount">{formatCurrency(cashFlowData.balance)}</p>
          <div className="kpi-card-details">
            <div className="kpi-detail-row kpi-detail-clickable" onClick={(e) => handleKpiLabelClick(e, 'cashflow', 'cashIn')}><span className="kpi-detail-label">Cash In</span><span className="kpi-detail-value" style={{color:'#059669'}}>{formatCurrency(cashFlowData.cashIn)}</span></div>
            <div className="kpi-detail-row kpi-detail-clickable" onClick={(e) => handleKpiLabelClick(e, 'cashflow', 'cashOut')}><span className="kpi-detail-label">Cash Out</span><span className="kpi-detail-value" style={{color:'#dc2626'}}>{formatCurrency(cashFlowData.cashOut)}</span></div>
            <div className="kpi-detail-row kpi-detail-clickable" onClick={(e) => handleKpiLabelClick(e, 'cashflow', 'balance')}><span className="kpi-detail-label">Balance</span><span className="kpi-detail-value">{formatCurrency(cashFlowData.balance)}</span></div>
          </div>
        </div>
      </div>

      {/* MAIN GRID */}
      <div className="dash-main-grid">
        {/* LEFT - Quick Actions + Module Overview */}
        <div>
          <div className="quick-actions-panel">
            <h4 className="panel-title">Quick Actions</h4>
            <div className="qa-grid">
              <button className="qa-btn qa-sale" onClick={() => handleCardClick('sale')}><span className="qa-dot"></span>New Sale</button>
              <button className="qa-btn qa-purchase" onClick={() => handleCardClick('purchase')}><span className="qa-dot"></span>New Purchase</button>
              <button className="qa-btn qa-payment" onClick={() => handleCardClick('payment')}><span className="qa-dot"></span>Payments</button>
              <button className="qa-btn qa-salereturn" onClick={() => handleCardClick('salereturn')}><span className="qa-dot"></span>Sale Return</button>
              <button className="qa-btn qa-vendor" onClick={() => handleCardClick('purchasereturn')}><span className="qa-dot"></span>Purchase Return</button>
              <button className="qa-btn qa-journal" onClick={() => handleCardClick('journal')}><span className="qa-dot"></span>Journal Entry</button>
            </div>
          </div>

          <div className="module-overview-panel">
            <h4 className="panel-title">Module Overview</h4>
            <div className="module-grid">
              <div className="module-item" onClick={() => handleCardClick('sale')}>
                <span className="module-item-count">{transactionStats.sales.count}</span>
                <span className="module-item-label">Sales</span>
                <span className="module-item-sub">{transactionStats.sales.count} this month</span>
              </div>
              <div className="module-item" onClick={() => handleCardClick('purchase')}>
                <span className="module-item-count">{transactionStats.purchases.count}</span>
                <span className="module-item-label">Purchases</span>
                <span className="module-item-sub">this month</span>
              </div>
              <div className="module-item" onClick={() => handleCardClick('salereturn')}>
                <span className="module-item-count">{transactionStats.saleReturns.count}</span>
                <span className="module-item-label">Sale Returns</span>
                <span className="module-item-sub">this month</span>
              </div>
              <div className="module-item" onClick={() => handleCardClick('purchasereturn')}>
                <span className="module-item-count">{transactionStats.purchaseReturns.count}</span>
                <span className="module-item-label">Purchase Returns</span>
                <span className="module-item-sub">this month</span>
              </div>
              <div className="module-item" onClick={() => handleCardClick('payment')}>
                <span className="module-item-count">{transactionStats.paymentReceipts.count}</span>
                <span className="module-item-label">Payment & Receipts</span>
                <span className="module-item-sub">this month</span>
              </div>
              <div className="module-item" onClick={() => handleCardClick('journal')}>
                <span className="module-item-count">{transactionStats.journalEntries.count}</span>
                <span className="module-item-label">Journal Entries</span>
                <span className="module-item-sub">this month</span>
              </div>
            </div>
          </div>
        </div>

        {/* CENTER - Chart + Activities */}
        <div className="center-content">
          <div className="chart-panel">
            <div className="chart-header">
              <h4 className="chart-title">Sales vs Purchases — {currentMonth}</h4>
              <div className="chart-legend">
                <span className="chart-legend-item"><span className="chart-legend-dot dot-sales"></span>Sales</span>
                <span className="chart-legend-item"><span className="chart-legend-dot dot-purchases"></span>Purchases</span>
              </div>
            </div>
            <div className="chart-body">
              <svg className="chart-svg" viewBox="0 0 900 220" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="salesBarGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#818cf8', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: '#4f46e5', stopOpacity: 1 }} />
                  </linearGradient>
                  <linearGradient id="purchaseBarGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#fbbf24', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: '#f59e0b', stopOpacity: 1 }} />
                  </linearGradient>
                  <filter id="barShadow" x="-10%" y="-10%" width="120%" height="130%">
                    <feDropShadow dx="0" dy="1" stdDeviation="1.5" floodColor="#000" floodOpacity="0.06" />
                  </filter>
                </defs>

                {/* Grid lines */}
                {[0, 1, 2, 3].map(i => {
                  const y = 25 + i * 45;
                  return <line key={`grid${i}`} x1="55" y1={y} x2="880" y2={y} stroke="#f0f0f0" strokeWidth="1" strokeDasharray="3,3" />;
                })}

                {/* Y-axis labels */}
                {(() => {
                  const maxAmt = Math.max(...dailySales.map(d => d.amount), ...dailyPurchases.map(d => d.amount), 1);
                  return [0, 1, 2, 3].map(i => {
                    const val = maxAmt - (maxAmt / 3) * i;
                    const y = 25 + i * 45 + 3;
                    return <text key={`y${i}`} x="48" y={y} textAnchor="end" fontSize="8" fill="#9ca3af" fontWeight="500">{formatCompact(val)}</text>;
                  });
                })()}

                {/* Baseline */}
                <line x1="55" y1="195" x2="880" y2="195" stroke="#e5e7eb" strokeWidth="1" />

                {/* Grouped Bars */}
                {dailySales.map((day, i) => {
                  const maxAmt = Math.max(...dailySales.map(d => d.amount), ...dailyPurchases.map(d => d.amount), 1);
                  const chartW = 825;
                  const groupW = chartW / dailySales.length;
                  const barW = groupW * 0.34;
                  const gap = groupW * 0.05;
                  const startX = 55 + i * groupW + (groupW - barW * 2 - gap) / 2;
                  const chartH = 170;

                  const saleH = (day.amount / maxAmt) * chartH;
                  const purchaseH = ((dailyPurchases[i]?.amount || 0) / maxAmt) * chartH;

                  return (
                    <g key={`bar${i}`} filter="url(#barShadow)">
                      {day.amount > 0 && (
                        <rect x={startX} y={195 - saleH} width={barW} height={saleH} fill="url(#salesBarGrad)" rx="3" ry="3" style={{ cursor: 'pointer' }} onClick={() => handleChartBarClick(day.date, 'sale')}>
                          <title>{day.label} Sale: {formatCurrency(day.amount)}</title>
                        </rect>
                      )}
                      {(dailyPurchases[i]?.amount || 0) > 0 && (
                        <rect x={startX + barW + gap} y={195 - purchaseH} width={barW} height={purchaseH} fill="url(#purchaseBarGrad)" rx="3" ry="3" style={{ cursor: 'pointer' }} onClick={() => handleChartBarClick(day.date, 'purchase')}>
                          <title>{day.label} Purchase: {formatCurrency(dailyPurchases[i]?.amount || 0)}</title>
                        </rect>
                      )}
                    </g>
                  );
                })}

                {/* X-axis labels */}
                {dailySales.filter((_, i) => i % 5 === 0).map((day, idx) => {
                  const chartW = 825;
                  const actualIdx = idx * 5;
                  const groupW = chartW / dailySales.length;
                  const x = 55 + actualIdx * groupW + groupW / 2;
                  return <text key={`x${idx}`} x={x} y="210" textAnchor="middle" fontSize="8.5" fill="#9ca3af" fontWeight="500">{day.label}</text>;
                })}
              </svg>
            </div>
          </div>

          {/* Recent Activities */}
          <div className="activities-panel">
            <div className="activities-header">
              <h4 className="activities-title">Recent Activities</h4>
              <button className="activities-viewall">View All →</button>
            </div>
            <div className="activities-list">
              {recentActivities.slice(0, 12).map((activity, index) => (
                <div key={index} className="activity-row" onClick={() => handleActivityClick(activity)}>
                  <span className="activity-dot" style={{ backgroundColor: activity.color }}></span>
                  <div className="activity-info">
                    <span className="activity-name">{activity.description}</span>
                  </div>
                  <span className="activity-amt">{formatCurrency(activity.amount)}</span>
                  <span className={`activity-status ${activity.status === 'PAID' ? 'status-paid' : activity.status === 'PARTIAL' ? 'status-partial' : activity.status === 'POSTED' ? 'status-paid' : 'status-pending'}`}>
                    {activity.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT - Aging + Alerts */}
        <div className="right-panel">
          {/* Receivable Aging */}
          <div className="aging-card">
            <div className="aging-header">
              <h4 className="aging-title">Receivable Aging</h4>
              <span className="aging-total">{formatCurrency(receivableAging.total)}</span>
            </div>
            <div className="aging-bars">
              {Object.entries(receivableAging.buckets).map(([key, val]) => (
                <div key={key} className="aging-bar-row">
                  <span className="aging-bar-label">{key} Days</span>
                  <div className="aging-bar-track">
                    <div className="aging-bar-fill fill-receivable" style={{ width: `${receivableAging.total > 0 ? (val / receivableAging.total * 100) : 0}%` }}></div>
                  </div>
                  <span className="aging-bar-pct">{receivableAging.total > 0 ? Math.round(val / receivableAging.total * 100) : 0}%</span>
                  <span className="aging-bar-amt">{formatCompact(val)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Payable Aging */}
          <div className="aging-card">
            <div className="aging-header">
              <h4 className="aging-title">Payable Aging</h4>
              <span className="aging-total">{formatCurrency(payableAging.total)}</span>
            </div>
            <div className="aging-bars">
              {Object.entries(payableAging.buckets).map(([key, val]) => (
                <div key={key} className="aging-bar-row">
                  <span className="aging-bar-label">{key} Days</span>
                  <div className="aging-bar-track">
                    <div className="aging-bar-fill fill-payable" style={{ width: `${payableAging.total > 0 ? (val / payableAging.total * 100) : 0}%` }}></div>
                  </div>
                  <span className="aging-bar-pct">{payableAging.total > 0 ? Math.round(val / payableAging.total * 100) : 0}%</span>
                  <span className="aging-bar-amt">{formatCompact(val)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Low Stock Items */}
          <div className="info-card">
            <div className="info-card-header">
              <h4 className="info-card-title"><span className="title-dot dot-amber"></span>Low Stock Items</h4>
              {stockSummary.lowStock > 0 && <span className="info-card-viewall" onClick={() => history.push('/StockReport')}>View All →</span>}
            </div>
            <p className="info-card-empty">{stockSummary.lowStock > 0 ? `${stockSummary.lowStock} items need attention` : 'No low stock items'}</p>
          </div>
        </div>
      </div>

      {/* Modals */}
      {openModal && modalType === 'sale' && (
        <SaleModal
          open={openModal}
          onClose={handleCloseModal}
          modalAction={modalAction}
          formData={formData}
          setFormData={setFormData}
          saleDetails={detailsData}
          setSaleDetails={setDetailsData}
          relatedItems={relatedItems}
          setRelatedItems={setRelatedItems}
          onSave={handleSave}
          customers={customers}
          items={items}
          banks={banks}
          loading={modalLoading}
          freightPackingGST={freightPackingGST}
          companyId={companyId}
          branchId={branchId}
          token={token}
        />
      )}

      {openModal && modalType === 'purchase' && (
        <PurchaseModal
          open={openModal}
          onClose={handleCloseModal}
          modalAction={modalAction}
          formData={formData}
          setFormData={setFormData}
          purchaseDetails={detailsData}
          setPurchaseDetails={setDetailsData}
          relatedItems={relatedItems}
          setRelatedItems={setRelatedItems}
          onSave={handleSave}
          vendors={vendors}
          items={items}
          loading={modalLoading}
          freightPackingGST={freightPackingGST}
          companyId={companyId}
          branchId={branchId}
          token={token}
        />
      )}

      {openModal && modalType === 'payment' && (
        <PaymentReceiptsModal
          open={openModal}
          onClose={handleCloseModal}
          modalAction={modalAction}
          formData={formData}
          setFormData={setFormData}
          paymentReceiptDetails={detailsData}
          setPaymentReceiptDetails={setDetailsData}
          onSave={handleSave}
          masters={masters}
          banks={banks}
          loading={modalLoading}
          companyId={companyId}
          branchId={branchId}
          token={token}
        />
      )}

      {openModal && modalType === 'journal' && (
        <JournalEntryModal
          open={openModal}
          onClose={handleCloseModal}
          modalAction={modalAction}
          formData={formData}
          setFormData={setFormData}
          journalEntryDetails={detailsData}
          setJournalEntryDetails={setDetailsData}
          onSave={handleSave}
          masters={masters}
          loading={modalLoading}
          companyId={companyId}
          branchId={branchId}
          token={token}
        />
      )}

      {/* Purchase Return View Modal */}
      <Dialog
        open={purchaseReturnViewModalOpen}
        onClose={() => setPurchaseReturnViewModalOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: '1px solid #e2e8f0',
          bgcolor: selectedPurchaseReturn?.IsActive === false ? '#fee2e2' : '#fafbfc',
          p: 2,
          pb: 1.5
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#0f172a', fontSize: '0.875rem' }}>
                  View Purchase Return
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                  Purchase return details and items
                </Typography>
              </Box>
              {selectedPurchaseReturn?.IsActive === false && (
                <Chip
                  label="INACTIVE"
                  size="small"
                  sx={{
                    bgcolor: '#991b1b',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    height: '20px',
                    letterSpacing: '0.5px'
                  }}
                />
              )}
            </Box>
            <IconButton
              onClick={() => setPurchaseReturnViewModalOpen(false)}
              size="small"
              sx={{
                bgcolor: selectedPurchaseReturn?.IsActive === false ? '#fecaca' : '#f1f5f9',
                '&:hover': { bgcolor: selectedPurchaseReturn?.IsActive === false ? '#fca5a5' : '#e2e8f0' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 1.5, bgcolor: selectedPurchaseReturn?.IsActive === false ? '#fef2f2' : '#fafbfc' }}>
          {selectedPurchaseReturn && (
            <>
              {/* Header Information */}
              <Box sx={{
                bgcolor: selectedPurchaseReturn?.IsActive === false ? '#fee2e2' : '#dbeafe',
                borderRadius: 1.5,
                p: 1,
                mb: 1.5,
                border: selectedPurchaseReturn?.IsActive === false ? '1px solid #fca5a5' : '1px solid #93c5fd',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={4} md={2}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      Return No
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.8rem' }}>
                      {selectedPurchaseReturn.BillNumber}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4} md={2}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      Return Date
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.75rem' }}>
                      {formatDate(selectedPurchaseReturn.BillDate)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4} md={2}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      Purchase No
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.75rem' }}>
                      {selectedPurchaseReturn.PurchaseNo || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.5}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      Supplier
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.75rem' }}>
                      {selectedPurchaseReturn.MasterName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={1.75}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      VAT Type
                    </Typography>
                    <Chip
                      label={selectedPurchaseReturn.VATCalculationType === 2 ? 'Including Tax' : 'Excluding Tax'}
                      size="small"
                      sx={{
                        bgcolor: selectedPurchaseReturn.VATCalculationType === 2 ? '#dcfce7' : '#dbeafe',
                        color: selectedPurchaseReturn.VATCalculationType === 2 ? '#166534' : '#1e40af',
                        fontWeight: 600,
                        fontSize: '0.65rem',
                        height: '20px'
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={1.75}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      Invoice Type
                    </Typography>
                    <Chip
                      label={selectedPurchaseReturn.InvoiceTypeName || 'N/A'}
                      size="small"
                      sx={{
                        bgcolor: '#fef3c7',
                        color: '#92400e',
                        fontWeight: 600,
                        fontSize: '0.65rem',
                        height: '20px'
                      }}
                    />
                  </Grid>
                  {selectedPurchaseReturn.Remarks && (
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                        Remarks
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem', fontStyle: 'italic' }}>
                        {selectedPurchaseReturn.Remarks}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Return Items Table */}
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.75, fontSize: '0.8rem' }}>
                  Return Items
                </Typography>
                <Box sx={{
                  bgcolor: 'white',
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: '1px solid #e2e8f0'
                }}>
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ bgcolor: selectedPurchaseReturn?.IsActive === false ? '#fee2e2' : '#dbeafe', borderBottom: selectedPurchaseReturn?.IsActive === false ? '2px solid #fca5a5' : '2px solid #93c5fd' }}>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>#</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>ITEM NAME</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>HSN CODE</th>
                          <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>QUANTITY</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>RATE</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>AMOUNT</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>DISC %</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>DISC AMT</th>
                          <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>VAT %</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>TAXABLE</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>VAT Amount</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>IGST</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>TOTAL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {purchaseReturnDetails.map((item, index) => (
                          <tr key={item.Id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '6px 8px' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                                {index + 1}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.7rem' }}>
                                {item.ItemName}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px' }}>
                              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                {item.HSNCode || '-'}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                              <Chip
                                label={item.Quantity}
                                size="small"
                                sx={{
                                  bgcolor: '#fef3c7',
                                  color: '#92400e',
                                  fontWeight: 700,
                                  fontSize: '0.65rem',
                                  minWidth: '45px',
                                  height: '20px'
                                }}
                              />
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                {formatCurrency(item.Rate)}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                {formatCurrency(item.Amount || 0)}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                {item.DiscountPercentage || 0}%
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ color: '#dc2626', fontSize: '0.7rem', fontWeight: 600 }}>
                                {formatCurrency(item.DiscountAmount || 0)}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                              <Chip
                                label={`${item.VATRate || item.GSTRate || 0}%`}
                                size="small"
                                sx={{
                                  bgcolor: '#d1fae5',
                                  color: '#065f46',
                                  fontWeight: 700,
                                  fontSize: '0.65rem',
                                  minWidth: '45px',
                                  height: '20px'
                                }}
                              />
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                {formatCurrency(item.TaxableAmount || 0)}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ color: '#dc2626', fontSize: '0.7rem', fontWeight: 600 }}>
                                {formatCurrency(item.VATAmount || 0)}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ color: '#dc2626', fontSize: '0.7rem', fontWeight: 600 }}>
                                {formatCurrency(item.IGSTAmount || 0)}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.7rem' }}>
                                {formatCurrency(item.TotalAmount || 0)}
                              </Typography>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              </Box>

              {/* Return Summary Section */}
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.75, fontSize: '0.8rem' }}>
                  Return Summary
                </Typography>
                <Grid container spacing={1.5}>
                  {/* Financial Summary */}
                  <Grid item xs={12} md={8}>
                    <Box sx={{
                      bgcolor: 'white',
                      borderRadius: 1,
                      p: 1,
                      border: '1px solid #e2e8f0'
                    }}>
                      <Grid container spacing={1}>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Subtotal
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedPurchaseReturn.Amount || 0)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Discount
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedPurchaseReturn.DiscountAmount || 0)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Taxable Amount
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedPurchaseReturn.TaxableAmount || 0)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#fee2e2', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#991b1b', fontSize: '0.6rem', fontWeight: 600 }}>
                              VAT Amount
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedPurchaseReturn.VATAmount || 0)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#fee2e2', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#991b1b', fontSize: '0.6rem', fontWeight: 600 }}>
                              IGST
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedPurchaseReturn.IGSTAmount || 0)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Freight
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedPurchaseReturn.FreightAmount || 0)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Freight VAT
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedPurchaseReturn.FreightVATAmount || 0)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Packing
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedPurchaseReturn.PackingAmount || 0)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Packing VAT
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedPurchaseReturn.PackingVATAmount || 0)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Round Off
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.2, fontSize: '0.75rem' }}>
                              {selectedPurchaseReturn.RoundOff >= 0 ? '+' : ''}{formatCurrency(selectedPurchaseReturn.RoundOff || 0)}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Total Summary */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{
                      bgcolor: 'white',
                      borderRadius: 1,
                      p: 1,
                      border: '1px solid #e2e8f0',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1
                    }}>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#dbeafe', borderRadius: 1, border: '2px solid #93c5fd' }}>
                        <Typography variant="caption" sx={{ color: '#1e40af', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase' }}>
                          Total Return Amount
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#2563eb', mt: 0.3, fontSize: '1.25rem' }}>
                          {formatCurrency(selectedPurchaseReturn.TotalAmount || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#fef3c7', borderRadius: 1, border: '2px solid #fde68a' }}>
                        <Typography variant="caption" sx={{ color: '#92400e', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase' }}>
                          Paid Amount
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#ea580c', mt: 0.3, fontSize: '0.95rem' }}>
                          {formatCurrency(selectedPurchaseReturn.PaidAmount || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#dcfce7', borderRadius: 1, border: '2px solid #86efac' }}>
                        <Typography variant="caption" sx={{ color: '#166534', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase' }}>
                          Balance Amount
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#16a34a', mt: 0.3, fontSize: '0.95rem' }}>
                          {formatCurrency(selectedPurchaseReturn.BalanceAmount || (selectedPurchaseReturn.TotalAmount - selectedPurchaseReturn.PaidAmount))}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </DialogContent>
        <Box sx={{
          p: 1,
          borderTop: '1px solid #e2e8f0',
          bgcolor: '#f8fafc',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <Button
            variant="outlined"
            onClick={() => setPurchaseReturnViewModalOpen(false)}
            sx={{
              textTransform: 'none',
              borderRadius: 1,
              px: 2.5,
              py: 0.5,
              fontWeight: 600,
              fontSize: '0.75rem',
              borderColor: '#3b82f6',
              color: '#3b82f6',
              '&:hover': {
                borderColor: '#2563eb',
                bgcolor: '#eff6ff'
              }
            }}
          >
            Close
          </Button>
        </Box>
      </Dialog>

      {/* Sale Return View Modal */}
      <SaleReturnModal
        open={saleReturnViewModalOpen}
        onClose={() => setSaleReturnViewModalOpen(false)}
        modalAction="view"
        selectedSaleReturn={selectedSaleReturn}
        saleReturnDetails={saleReturnDetails}
      />

      {/* Modern Loading Overlay */}
      <ModernLoadingOverlay
        open={modalLoading}
        message="Loading transaction details..."
      />

      {/* KPI Detail Modal */}
      <Dialog
        open={kpiModalOpen}
        onClose={() => setKpiModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxHeight: '85vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, borderBottom: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>
            {kpiModalTitle}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label={`${kpiModalData.length} records`} size="small" sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 600, fontSize: '0.7rem' }} />
            <IconButton onClick={() => setKpiModalOpen(false)} size="small" sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {kpiModalData.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center', color: '#6b7280' }}>
              <Typography sx={{ fontSize: '0.9rem' }}>No data found for this filter.</Typography>
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(135deg, #1e293b, #334155)' }}>
                    {kpiModalColumns.map((col, idx) => (
                      <th key={idx} style={{ padding: '10px 12px', color: 'white', textAlign: col.align || 'left', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {kpiModalData.map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbfc', transition: 'background-color 0.15s' }}>
                      {kpiModalColumns.map((col, colIdx) => {
                        let value = '';
                        if (col.key === 'index') {
                          value = idx + 1;
                        } else {
                          value = item[col.key] || (col.fallback ? item[col.fallback] : '') || '';
                        }

                        // Format value
                        if (col.format === 'currency' && col.key !== 'index') {
                          const numVal = parseFloat(value) || 0;
                          const currName = item.CurrencyName || '';
                          const getCurrencySymbol = (n) => {
                            if (!n) return 'د.إ';
                            const lower = n.toLowerCase().trim();
                            const symbolMap = {
                              'inr': '₹', 'rupee': '₹', 'indian rupee': '₹',
                              'usd': '$', 'dollar': '$', 'us dollar': '$',
                              'eur': '€', 'euro': '€',
                              'gbp': '£', 'pound': '£', 'british pound': '£',
                              'aed': 'د.إ', 'dirham': 'د.إ', 'uae dirham': 'د.إ',
                              'sar': '﷼', 'riyal': '﷼', 'saudi riyal': '﷼',
                              'jpy': '¥', 'yen': '¥',
                              'cny': '¥', 'yuan': '¥', 'rmb': '¥',
                              'aud': 'A$', 'australian dollar': 'A$',
                              'cad': 'C$', 'canadian dollar': 'C$',
                              'sgd': 'S$', 'singapore dollar': 'S$',
                              'omr': 'OMR', 'omani rial': 'OMR',
                              'qar': 'QR', 'qatari riyal': 'QR',
                              'kwd': 'KD', 'kuwaiti dinar': 'KD',
                              'bhd': 'BD', 'bahraini dinar': 'BD'
                            };
                            if (symbolMap[lower]) return symbolMap[lower];
                            for (const [key, symbol] of Object.entries(symbolMap)) {
                              if (lower.includes(key)) return symbol;
                            }
                            return n;
                          };
                          const symbol = getCurrencySymbol(currName);
                          value = `${symbol} ${numVal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                        } else if (col.format === 'date' && value) {
                          value = new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                        }

                        return (
                          <td key={colIdx} style={{ padding: '8px 12px', textAlign: col.align || 'left', fontWeight: col.key === 'BalanceAmount' || col.format === 'currency' ? 600 : 400, color: col.key === 'BalanceAmount' ? '#dc2626' : col.key === 'DueDays' ? '#92400e' : '#334155', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                            {col.key === 'DueDays' ? (
                              <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', background: (parseInt(value) || 0) > 60 ? '#fee2e2' : (parseInt(value) || 0) > 30 ? '#fef3c7' : '#d1fae5', color: (parseInt(value) || 0) > 60 ? '#991b1b' : (parseInt(value) || 0) > 30 ? '#92400e' : '#065f46' }}>
                                {value} days
                              </span>
                            ) : col.key === 'CurrencyName' ? (
                              <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', background: '#e0f7fa', color: '#006064' }}>
                                {value || 'AED'}
                              </span>
                            ) : value}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
                {kpiModalColumns.some(col => col.format === 'currency' && (col.key === 'BalanceAmount' || col.key === 'SaleAmount' || col.key === 'PurchaseAmount' || col.key === 'TotalAmount')) && (
                  <tfoot>
                    <tr style={{ backgroundColor: '#f1f5f9', fontWeight: 700 }}>
                      {kpiModalColumns.map((col, idx) => {
                        if (col.format === 'currency') {
                          const total = kpiModalData.reduce((sum, item) => sum + (parseFloat(item[col.key]) || 0), 0);
                          return (
                            <td key={idx} style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, fontSize: '0.75rem', color: col.key === 'BalanceAmount' ? '#dc2626' : '#0f172a' }}>
                              {total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          );
                        }
                        if (idx === 0) {
                          return <td key={idx} style={{ padding: '10px 12px', fontWeight: 700, fontSize: '0.75rem' }}>Total ({kpiModalData.length})</td>;
                        }
                        return <td key={idx}></td>;
                      })}
                    </tr>
                  </tfoot>
                )}
              </table>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Chart Drill-Down Modal */}
      <Dialog
        open={chartModalOpen}
        onClose={() => setChartModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxHeight: '80vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
            {chartModalType === 'sale' ? '📦 Sales' : '🛒 Purchases'} — {chartModalDate ? new Date(chartModalDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
          </Typography>
          <IconButton onClick={() => setChartModalOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {chartModalItems.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center', color: '#6b7280' }}>
              <Typography>No {chartModalType === 'sale' ? 'sales' : 'purchases'} found for this date.</Typography>
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: chartModalType === 'sale' ? 'linear-gradient(135deg, #0f3460, #1a1a2e)' : 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                    <th style={{ padding: '8px 12px', color: 'white', textAlign: 'left', fontWeight: 600 }}>#</th>
                    <th style={{ padding: '8px 12px', color: 'white', textAlign: 'left', fontWeight: 600 }}>Bill No</th>
                    <th style={{ padding: '8px 12px', color: 'white', textAlign: 'left', fontWeight: 600 }}>{chartModalType === 'sale' ? 'Customer' : 'Vendor'}</th>
                    <th style={{ padding: '8px 12px', color: 'white', textAlign: 'right', fontWeight: 600 }}>Amount</th>
                    <th style={{ padding: '8px 12px', color: 'white', textAlign: 'center', fontWeight: 600 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {chartModalItems.map((item, idx) => {
                    const totalAmt = parseFloat(item.TotalAmount) || 0;
                    const balanceAmt = parseFloat(item.BalanceAmount) || 0;
                    const status = balanceAmt <= 0 ? 'PAID' : balanceAmt < totalAmt ? 'PARTIAL' : 'PENDING';
                    return (
                      <tr key={item.Id || idx} style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                        <td style={{ padding: '8px 12px' }}>{idx + 1}</td>
                        <td style={{ padding: '8px 12px', fontWeight: 500 }}>{item.BillNumber || item.BillNo || '-'}</td>
                        <td style={{ padding: '8px 12px' }}>{item.MasterName || item.CustomerName || item.VendorName || '-'}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>{totalAmt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <span style={{
                            fontSize: '9px',
                            fontWeight: 700,
                            padding: '2px 6px',
                            borderRadius: '3px',
                            background: status === 'PAID' ? '#d1fae5' : status === 'PARTIAL' ? '#fef3c7' : '#fee2e2',
                            color: status === 'PAID' ? '#065f46' : status === 'PARTIAL' ? '#92400e' : '#991b1b'
                          }}>
                            {status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ backgroundColor: '#f3f4f6', fontWeight: 700 }}>
                    <td colSpan={3} style={{ padding: '8px 12px' }}>Total ({chartModalItems.length} {chartModalItems.length === 1 ? 'bill' : 'bills'})</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      {chartModalItems.reduce((sum, item) => sum + (parseFloat(item.TotalAmount) || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Home;
